import Phaser from "phaser";
import { TILE_W, TILE_H } from "../core/iso";

/**
 * Librería de "props" isométricos dibujados por vectores (placeholders
 * estilizados, sustituibles por spritesheets después). Separada de la
 * lógica: cada función devuelve un GameObject cuyo origen (0,0) es la
 * BASE-CENTRO del objeto, lista para colocarse en iso(x,y) con depth.
 *
 * Nomenclatura de assets pensada para el reemplazo futuro:
 *   construction_floor_*, scaffold_01, wood_pallet_01, safety_locker,
 *   worker_*, hazard_fallen_tool, etc.
 */

const HW = TILE_W / 2; // 64
const HH = TILE_H / 2; // 32

/** Sombra elíptica suave (punto 17). */
export function shadow(scene: Phaser.Scene, rx = HW * 0.7, ry = HH * 0.55): Phaser.GameObjects.Ellipse {
  return scene.add.ellipse(0, 0, rx * 2, ry * 2, 0x1a1206, 0.22);
}

function darken(hex: number, f: number): number {
  const r = Math.max(0, Math.min(255, Math.round(((hex >> 16) & 255) * f)));
  const g = Math.max(0, Math.min(255, Math.round(((hex >> 8) & 255) * f)));
  const b = Math.max(0, Math.min(255, Math.round((hex & 255) * f)));
  return (r << 16) | (g << 8) | b;
}

/**
 * Caja isométrica (el "ladrillo" de casi todo: cajas, palés, hormigón,
 * contenedores, taquillas...). hw/hd = medio ancho/fondo en px, ph = alto.
 */
export function isoBox(
  g: Phaser.GameObjects.Graphics,
  hw: number,
  hd: number,
  ph: number,
  top: number,
  opts: { stroke?: number; ox?: number; oy?: number } = {},
): void {
  const ox = opts.ox ?? 0;
  const oy = opts.oy ?? 0;
  const left = darken(top, 0.72);
  const right = darken(top, 0.55);
  const N = [ox, oy - hd - ph];
  const E = [ox + hw, oy - ph];
  const S = [ox, oy + hd - ph];
  const W = [ox - hw, oy - ph];
  const E0 = [ox + hw, oy];
  const S0 = [ox, oy + hd];
  const W0 = [ox - hw, oy];
  const face = (pts: number[][], c: number) => {
    g.fillStyle(c, 1);
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
    g.closePath();
    g.fillPath();
    if (opts.stroke != null) {
      g.lineStyle(1.5, opts.stroke, 0.5);
      g.strokePath();
    }
  };
  face([W, S, S0, W0], left); // cara izquierda (SO)
  face([E, S, S0, E0], right); // cara derecha (SE)
  face([N, E, S, W], top); // cara superior
}

/**
 * Punto sobre una cara vertical de un isoBox, en coordenadas locales de la cara:
 *  u = 0 (esquina lateral) → 1 (esquina frontal central); v = 0 (arriba) → 1 (abajo).
 * Permite pegar ventanas, puertas y carteles SIGUIENDO la perspectiva isométrica.
 */
export function facePt(side: "SE" | "SO", u: number, v: number, hw: number, hd: number, ph: number): [number, number] {
  const x = side === "SE" ? hw * (1 - u) : -hw + hw * u;
  const y = -ph + u * hd + v * ph;
  return [x, y];
}

/** Panel (cuadrilátero) sobre una cara del isoBox, en coordenadas de cara. */
export function facePanel(
  g: Phaser.GameObjects.Graphics,
  side: "SE" | "SO",
  hw: number, hd: number, ph: number,
  u0: number, u1: number, v0: number, v1: number,
  fill: number,
  opts: { alpha?: number; stroke?: number; sw?: number; sa?: number } = {},
): void {
  const pts = [
    facePt(side, u0, v0, hw, hd, ph),
    facePt(side, u1, v0, hw, hd, ph),
    facePt(side, u1, v1, hw, hd, ph),
    facePt(side, u0, v1, hw, hd, ph),
  ];
  g.fillStyle(fill, opts.alpha ?? 1);
  g.beginPath();
  g.moveTo(pts[0][0], pts[0][1]);
  for (let i = 1; i < 4; i++) g.lineTo(pts[i][0], pts[i][1]);
  g.closePath();
  g.fillPath();
  if (opts.stroke != null) { g.lineStyle(opts.sw ?? 1.5, opts.stroke, opts.sa ?? 1); g.strokePath(); }
}

/** Línea sobre una cara del isoBox (para junquillos de ventana, etc.). */
export function faceLine(
  g: Phaser.GameObjects.Graphics,
  side: "SE" | "SO",
  hw: number, hd: number, ph: number,
  u0: number, v0: number, u1: number, v1: number,
  color: number, w = 1.5, a = 1,
): void {
  const p0 = facePt(side, u0, v0, hw, hd, ph);
  const p1 = facePt(side, u1, v1, hw, hd, ph);
  g.lineStyle(w, color, a);
  g.beginPath(); g.moveTo(p0[0], p0[1]); g.lineTo(p1[0], p1[1]); g.strokePath();
}

function container(scene: Phaser.Scene, draw: (g: Phaser.GameObjects.Graphics) => void, withShadow = true, sx = HW * 0.7, sy = HH * 0.55): Phaser.GameObjects.Container {
  const parts: Phaser.GameObjects.GameObject[] = [];
  if (withShadow) parts.push(shadow(scene, sx, sy));
  const g = scene.add.graphics();
  draw(g);
  parts.push(g);
  return scene.add.container(0, 0, parts);
}

/** Línea de brillo (borde iluminado, coherente con luz arriba-izquierda). */
function hiLine(g: Phaser.GameObjects.Graphics, x1: number, y1: number, x2: number, y2: number, c = 0xffffff, a = 0.22, w = 1.5): void {
  g.lineStyle(w, c, a);
  g.beginPath();
  g.moveTo(x1, y1);
  g.lineTo(x2, y2);
  g.strokePath();
}

/** Remaches/tornillos: puntitos de detalle metálico. */
function rivets(g: Phaser.GameObjects.Graphics, pts: [number, number][], c = 0x2a2a30, r = 1.4): void {
  g.fillStyle(c, 1);
  for (const [x, y] of pts) g.fillCircle(x, y, r);
}

/** Mancha de contacto con el suelo (oscurece la base para asentar el objeto). */
function contact(g: Phaser.GameObjects.Graphics, rx = 26, ry = 9): void {
  g.fillStyle(0x1a1206, 0.18);
  g.fillEllipse(0, 2, rx * 2, ry * 2);
}

// ---------------- SUELO ----------------
export type TileKind = "concrete" | "dirt" | "sand" | "planks" | "hazard" | "path";

const TILE_COLORS: Record<TileKind, [number, number]> = {
  concrete: [0xb7b1a2, 0xbfb9aa], // hormigón gris cálido
  dirt: [0x8f7550, 0x977c57],     // tierra
  sand: [0xc7b285, 0xd0bc90],     // arena
  planks: [0xa87f45, 0xb2884d],   // acceso de tablas
  hazard: [0xc9a83a, 0xd4b23e],   // banda de precaución (uso puntual)
  path: [0xa89a7e, 0xafa184],     // camino compactado
};

/** Vértices del rombo del tile: N (sx,sy), E, S, W, y centro. */
function tileDiamond(g: Phaser.GameObjects.Graphics, sx: number, sy: number): void {
  g.beginPath();
  g.moveTo(sx, sy);
  g.lineTo(sx + HW, sy + HH);
  g.lineTo(sx, sy + TILE_H);
  g.lineTo(sx - HW, sy + HH);
  g.closePath();
}

/** Pinta un tile del suelo con textura sutil y borde casi invisible (suelo continuo). */
export function paintTile(g: Phaser.GameObjects.Graphics, sx: number, sy: number, kind: TileKind, hx: number, hy: number): void {
  const [base, top] = TILE_COLORS[kind];
  const h = (hx * 928371 + hy * 12347) & 255; // hash determinista
  const cx = sx, cy = sy + HH; // centro del tile
  // base con ligerísima variación por tile
  const c = h % 6 === 0 ? top : (h % 11 === 0 ? darken(base, 0.94) : base);
  g.fillStyle(c, 1);
  tileDiamond(g, sx, sy); g.fillPath();
  // junta muy sutil (no cuadrícula marcada)
  g.lineStyle(1, darken(base, 0.9), 0.18);
  tileDiamond(g, sx, sy); g.strokePath();

  if (kind === "planks") {
    // tablas: 3 seams + veta + clavos
    g.lineStyle(1.4, darken(base, 0.68), 0.6);
    for (let i = -1; i <= 1; i++) { g.beginPath(); g.moveTo(sx - HW + 16, cy + i * 12); g.lineTo(sx + HW - 16, cy + i * 12); g.strokePath(); }
    if (h % 3 === 0) { g.fillStyle(darken(base, 0.7), 0.6); g.fillCircle(cx - 20, cy - 10, 1); g.fillCircle(cx + 18, cy + 8, 1); }
  } else if (kind === "concrete") {
    // juntas de dilatación + moteado fino + grieta ocasional
    g.lineStyle(1, darken(base, 0.86), 0.3);
    g.beginPath(); g.moveTo(cx - HW + 6, cy - HH * 0.3); g.lineTo(cx + HW - 6, cy + HH * 0.3); g.strokePath();
    g.fillStyle(darken(base, 0.9), 0.35);
    for (let k = 0; k < 3; k++) { const hh = (h >> (k * 2)) & 63; g.fillCircle(cx - 24 + hh, cy - 10 + ((h >> k) % 20), 1); }
    if (h % 9 === 0) { g.lineStyle(1, darken(base, 0.75), 0.35); g.beginPath(); g.moveTo(cx - 8, cy - 6); g.lineTo(cx + 2, cy + 2); g.lineTo(cx - 2, cy + 8); g.strokePath(); }
  } else if (kind === "path") {
    // camino compactado: dos rodadas + grava
    g.lineStyle(3, darken(base, 0.82), 0.28);
    g.beginPath(); g.moveTo(cx - HW * 0.7, cy - 4); g.lineTo(cx + HW * 0.7, cy + 4); g.strokePath();
    g.beginPath(); g.moveTo(cx - HW * 0.7, cy + 6); g.lineTo(cx + HW * 0.7, cy + 14); g.strokePath();
    g.fillStyle(darken(base, 0.8), 0.4);
    for (let k = 0; k < 4; k++) { g.fillCircle(cx - 26 + ((h >> k) % 52), cy - 8 + ((h >> (k + 1)) % 18), 1); }
  } else if (kind === "hazard") {
    // banda de precaución: franjas diagonales amarillo/negro (uso puntual)
    g.fillStyle(0x2b2b30, 0.9);
    for (let i = -2; i <= 2; i++) {
      const off = i * 16;
      g.beginPath();
      g.moveTo(sx - HW + off, cy + HH * 0.0); g.lineTo(sx - HW + off + 8, cy - 4);
      g.lineTo(sx + off + 8, cy + HH); g.lineTo(sx + off, cy + HH + 4);
      g.closePath(); g.fillPath();
    }
  } else {
    // tierra / arena: clods, piedrecitas y manchas
    if (h % 3 === 0) { g.fillStyle(darken(base, 0.82), 0.4); g.fillEllipse(cx - 12 + (h % 24), cy - 4 + ((h >> 2) % 14), 8, 4); }
    g.fillStyle(darken(base, 0.7), 0.5);
    for (let k = 0; k < 3; k++) if (((h >> k) & 3) === 0) g.fillCircle(cx - 20 + ((h >> k) % 40), cy - 8 + ((h >> (k + 1)) % 18), 1.4);
    if (kind === "sand" && h % 5 === 0) { g.lineStyle(1, darken(base, 0.88), 0.3); g.beginPath(); g.moveTo(cx - 16, cy); g.lineTo(cx + 16, cy + 4); g.strokePath(); }
  }
}

// ---------------- PROPS ----------------
export function pallet(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // palé europeo: tacos, tablas del deck con huecos, clavos y veta
  return container(scene, (g) => {
    contact(g, 48, 15);
    // tacos (patas)
    g.fillStyle(0x5a3f22, 1);
    for (const bx of [-40, -6, 28]) g.fillRect(bx, -9, 12, 9);
    // cuerpo
    isoBox(g, 46, 24, 6, 0xa9803f, { stroke: 0x6e4f2c, oy: -9 });
    // tablas del deck (grooves) paralelas al eje SO-NE
    g.lineStyle(2, 0x6e4f2c, 0.85);
    for (let k = -2; k <= 2; k++) {
      const ox = k * 11;
      g.beginPath(); g.moveTo(ox - 23, -9 - 6 + 12); g.lineTo(ox + 23, -9 - 6 - 12); g.strokePath();
    }
    // clavos + veta
    rivets(g, [[-34, -18], [-2, -14], [30, -18], [-18, -22], [14, -22]], 0x4a3018, 1);
    g.lineStyle(1, 0x8a6636, 0.5);
    g.beginPath(); g.moveTo(-40, -13); g.lineTo(-20, -19); g.moveTo(6, -12); g.lineTo(26, -18); g.strokePath();
  });
}

export function crate(scene: Phaser.Scene, color = 0xb98a4a): Phaser.GameObjects.Container {
  // caja de madera: refuerzos en Z, esquineras metálicas y etiqueta
  return container(scene, (g) => {
    contact(g, 30, 10);
    isoBox(g, 30, 20, 34, color, { stroke: 0x5a3c1c });
    // refuerzos diagonales (Z) en las caras
    g.lineStyle(2.5, darken(color, 0.72), 0.85);
    g.beginPath(); g.moveTo(-30, -34); g.lineTo(0, 20); g.moveTo(-30, 0); g.lineTo(0, -14); g.strokePath();   // cara izq
    g.beginPath(); g.moveTo(30, -34); g.lineTo(0, 20); g.moveTo(30, 0); g.lineTo(0, -14); g.strokePath();       // cara der
    // esquineras metálicas
    g.fillStyle(0x6a6f77, 1);
    g.fillRect(-31, -2, 6, 4); g.fillRect(25, -2, 6, 4); g.fillRect(-3, 16, 6, 4);
    rivets(g, [[-28, -1], [28, -1], [0, 18]], 0x3a3f47, 1);
    // etiqueta con símbolo frágil
    g.fillStyle(0xf4ead0, 0.9); g.fillRect(-9, -28, 16, 11);
    g.lineStyle(1, 0xb23a2a, 0.9); g.strokeRect(-9, -28, 16, 11);
    g.fillStyle(0xb23a2a, 0.85); g.fillTriangle(-1, -27, -5, -19, 3, -19);
  });
}

export function crateStack(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // dos cajas apiladas con refuerzos y esquineras
  return container(scene, (g) => {
    contact(g, 34, 12);
    // caja inferior
    isoBox(g, 34, 22, 30, 0xa9803f, { stroke: 0x5a3c1c, oy: 0 });
    g.lineStyle(2.2, 0x6e4f2c, 0.8);
    g.beginPath(); g.moveTo(-34, -30); g.lineTo(0, 22); g.moveTo(34, -30); g.lineTo(0, 22); g.strokePath();
    g.fillStyle(0x6a6f77, 1); g.fillRect(-35, -2, 5, 4); g.fillRect(30, -2, 5, 4);
    // caja superior
    isoBox(g, 26, 17, 26, 0xc0925a, { stroke: 0x5a3c1c, oy: -30 });
    g.lineStyle(2, 0x6e4f2c, 0.8);
    g.beginPath(); g.moveTo(-26, -56); g.lineTo(0, -18); g.moveTo(26, -56); g.lineTo(0, -18); g.strokePath();
    g.fillStyle(0xf4ead0, 0.9); g.fillRect(-8, -46, 14, 9);
  });
}

export function sacks(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // sacos de cemento apilados: pliegues, etiqueta y polvo
  return container(scene, (g) => {
    contact(g, 30, 11);
    const bag = (ox: number, oy: number, c: number, w = 48, h = 22) => {
      g.fillStyle(darken(c, 0.72), 1); g.fillEllipse(ox + 2, oy + 4, w, h);        // sombra propia
      g.fillStyle(c, 1); g.fillEllipse(ox, oy, w, h);                              // cuerpo
      g.fillStyle(Math.min(0xffffff, darken(c, 1.14)) & 0xffffff, 1);
      g.fillEllipse(ox - 3, oy - 4, w * 0.72, h * 0.55);                           // luz arriba-izq
      // pliegues de los extremos (donde se cierra el saco)
      g.lineStyle(1.4, darken(c, 0.62), 0.7);
      g.beginPath(); g.moveTo(ox - w / 2 + 5, oy - 5); g.lineTo(ox - w / 2 + 11, oy + 4); g.strokePath();
      g.beginPath(); g.moveTo(ox + w / 2 - 5, oy - 5); g.lineTo(ox + w / 2 - 11, oy + 4); g.strokePath();
      // banda impresa (marca) + polvo
      g.fillStyle(0xb23a2a, 0.85); g.fillRect(ox - 11, oy - 5, 22, 8);
      g.fillStyle(0x2b2b30, 0.55); g.fillRect(ox - 8, oy - 2, 16, 2);
      g.fillStyle(0xffffff, 0.14); g.fillCircle(ox + 7, oy - 3, 1.6); g.fillCircle(ox - 9, oy + 2, 1.2);
    };
    bag(-14, 6, 0xcdbf9c);
    bag(16, 8, 0xc4b892);
    bag(1, -10, 0xe0d3b2);
  }, true, 30, 11);
}

export function pipes(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // tubos metálicos tumbados: cuerpo con brillo + anillo hueco en el extremo
  return container(scene, (g) => {
    contact(g, 40, 11);
    const tube = (ox: number, oy: number, c: number) => {
      // cuerpo del cilindro (se aleja hacia arriba-derecha)
      g.fillStyle(darken(c, 0.7), 1); g.fillRect(ox - 42, oy - 11, 84, 22);
      g.fillStyle(c, 1); g.fillRect(ox - 42, oy - 11, 84, 13);
      g.fillStyle(Math.min(0xffffff, darken(c, 1.25)) & 0xffffff, 1); g.fillRect(ox - 42, oy - 11, 84, 4); // brillo
      // extremo derecho (tapa)
      g.fillStyle(darken(c, 0.85), 1); g.fillEllipse(ox + 42, oy - 0, 14, 22);
      // extremo izquierdo (anillo hueco)
      g.fillStyle(darken(c, 0.6), 1); g.fillEllipse(ox - 42, oy, 15, 22);
      g.fillStyle(0x2c3236, 1); g.fillEllipse(ox - 42, oy, 8, 13);
      g.lineStyle(1.4, Math.min(0xffffff, darken(c, 1.2)) & 0xffffff, 0.5); g.strokeEllipse(ox - 42, oy, 15, 22);
    };
    // pila piramidal: 2 abajo + 1 arriba
    tube(-2, 8, 0x8a9299);
    tube(4, 8, 0x9aa2a9);
    tube(1, -10, 0x99a1a8);
  }, true, 40, 11);
}

export function barrel(scene: Phaser.Scene, color = 0xc4632a): Phaser.GameObjects.Container {
  // bidón metálico: cuerpo abombado, aros, tapa con brillo, tapón y etiqueta
  return container(scene, (g) => {
    contact(g, 24, 9);
    // cuerpo (lados con sombreado)
    g.fillStyle(darken(color, 0.68), 1); g.fillRect(-22, -48, 44, 48);
    g.fillStyle(color, 1); g.fillRect(-22, -48, 30, 48);
    g.fillStyle(Math.min(0xffffff, darken(color, 1.15)) & 0xffffff, 0.5); g.fillRect(-18, -46, 7, 44); // brillo vertical
    // base
    g.fillStyle(darken(color, 0.55), 1); g.fillEllipse(0, 0, 44, 18);
    // aros de refuerzo
    g.lineStyle(3, darken(color, 0.5), 0.9); g.strokeEllipse(0, -32, 44, 15); g.strokeEllipse(0, -16, 44, 15);
    // tapa superior (elipse con brillo)
    g.fillStyle(darken(color, 0.85), 1); g.fillEllipse(0, -48, 44, 18);
    g.fillStyle(Math.min(0xffffff, darken(color, 1.18)) & 0xffffff, 0.6); g.fillEllipse(-6, -50, 22, 8);
    g.lineStyle(1.6, darken(color, 0.5), 0.8); g.strokeEllipse(0, -48, 44, 18);
    // tapón (bung)
    g.fillStyle(darken(color, 0.5), 1); g.fillCircle(10, -48, 4);
    // etiqueta
    g.fillStyle(0xf4ead0, 0.9); g.fillRect(-11, -30, 22, 12);
    g.lineStyle(1, 0xb23a2a, 0.9); g.strokeRect(-11, -30, 22, 12);
    g.fillStyle(0xb23a2a, 0.85); g.fillTriangle(0, -29, -4, -21, 4, -21);
  }, true, 24, 9);
}

export function cone(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // cono de seguridad: base romboidal, cuerpo a dos tonos, banda reflectante, brillo
  return container(scene, (g) => {
    contact(g, 18, 7);
    // base cuadrada en iso
    g.fillStyle(0xb84e0d, 1);
    g.beginPath(); g.moveTo(0, 7); g.lineTo(21, -1); g.lineTo(0, -9); g.lineTo(-21, -1); g.closePath(); g.fillPath();
    g.lineStyle(1.4, 0x8a3a08, 0.6); g.strokePath();
    g.fillStyle(0xd8621a, 1);
    g.beginPath(); g.moveTo(0, 2); g.lineTo(13, -2); g.lineTo(0, -6); g.lineTo(-13, -2); g.closePath(); g.fillPath();
    // cuerpo cónico (luz izquierda / sombra derecha)
    g.fillStyle(0xd25a12, 1); g.fillTriangle(0, -46, 1, -4, 15, -2);
    g.fillStyle(0xf07a24, 1); g.fillTriangle(0, -46, -15, -2, 1, -4);
    // banda reflectante (trapecio siguiendo el cono)
    const wAt = (y: number) => (15 * (y + 46)) / 44;
    g.fillStyle(0xf4f0e6, 1);
    g.beginPath();
    g.moveTo(-wAt(-30), -30); g.lineTo(wAt(-30), -30); g.lineTo(wAt(-18), -18); g.lineTo(-wAt(-18), -18);
    g.closePath(); g.fillPath();
    // punta y brillo
    g.fillStyle(0xc9560f, 1); g.fillTriangle(0, -46, -3, -38, 3, -38);
    hiLine(g, -2, -44, -9, -18, 0xffffff, 0.28, 1.8);
  }, true, 18, 7);
}

export function fence(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // valla portátil de obra: pies, marco amarillo, barrotes y banda reflectante
  return container(scene, (g) => {
    contact(g, 48, 8);
    // pies de hormigón/plástico
    g.fillStyle(0x3a3f47, 1); g.fillEllipse(-40, 1, 24, 9); g.fillEllipse(40, 1, 24, 9);
    g.fillStyle(0x565b63, 1); g.fillRect(-51, -7, 22, 7); g.fillRect(29, -7, 22, 7);
    // postes
    g.fillStyle(0x8a6f2a, 1); g.fillRect(-46, -48, 6, 46); g.fillRect(40, -48, 6, 46);
    g.fillStyle(0xe0c542, 1); g.fillRect(-46, -48, 2, 46); g.fillRect(40, -48, 2, 46);
    // marco (barras superior/inferior)
    g.fillStyle(0xd8b93f, 1); g.fillRect(-46, -48, 92, 6); g.fillRect(-46, -10, 92, 5);
    hiLine(g, -44, -47, 44, -47, 0xffffff, 0.3, 1.5);
    // barrotes verticales
    g.fillStyle(0xc9a92e, 1);
    for (let x = -36; x <= 36; x += 10) g.fillRect(x, -42, 3, 32);
    // banda reflectante roja intermitente
    g.fillStyle(0xb23a2a, 0.9);
    for (let x = -44; x < 44; x += 16) g.fillRect(x, -47, 8, 4);
  }, false);
}

export function warningSign(scene: Phaser.Scene, kind: "fall" | "generic" = "generic"): Phaser.GameObjects.Container {
  // señal triangular sobre poste con trípode
  return container(scene, (g) => {
    contact(g, 16, 7);
    // trípode
    g.lineStyle(3, 0x3a3f47, 1);
    g.beginPath(); g.moveTo(0, -2); g.lineTo(-11, 5); g.moveTo(0, -2); g.lineTo(11, 5); g.moveTo(0, -2); g.lineTo(0, 4); g.strokePath();
    // poste
    g.fillStyle(0x6a6f77, 1); g.fillRect(-2.5, -40, 5, 40);
    g.fillStyle(0x8a9098, 1); g.fillRect(-2.5, -40, 2, 40);
    // triángulo con reborde oscuro
    g.fillStyle(0x241d14, 1); g.fillTriangle(0, -70, -23, -31, 23, -31);
    g.fillStyle(0xf0c400, 1); g.fillTriangle(0, -66, -18, -34, 18, -34);
    hiLine(g, -2, -63, -13, -36, 0xffffff, 0.25, 1.5);
    // símbolo
    g.fillStyle(0x241d14, 1);
    if (kind === "fall") {
      // persona cayendo + borde
      g.fillCircle(-4, -55, 2.6); g.fillRect(-6, -52, 3, 9); g.fillRect(-1, -49, 8, 2.5); g.fillRect(2, -60, 2.4, 8);
    } else {
      g.fillRect(-2, -56, 4, 12); g.fillCircle(0, -40, 2.3);
    }
  }, true, 16, 7);
}

export function ladder(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // escalera de mano de aluminio, apoyada e inclinada
  return container(scene, (g) => {
    contact(g, 16, 7);
    const c = 0xc9cdd2, cD = 0x8a9098;
    // largueros (aluminio, con lado en sombra)
    g.lineStyle(6, c, 1);
    g.beginPath(); g.moveTo(-26, 4); g.lineTo(18, -78); g.strokePath();
    g.beginPath(); g.moveTo(-12, 10); g.lineTo(32, -72); g.strokePath();
    g.lineStyle(2, cD, 0.7);
    g.beginPath(); g.moveTo(-24, 4); g.lineTo(20, -78); g.strokePath();
    // peldaños con grosor
    for (let i = 0; i < 7; i++) {
      const t = i / 6;
      const x1 = -26 + t * 44, y1 = 4 - t * 82, x2 = -12 + t * 44, y2 = 10 - t * 82;
      g.lineStyle(4, 0xaeb3b8, 1); g.beginPath(); g.moveTo(x1, y1); g.lineTo(x2, y2); g.strokePath();
      g.lineStyle(1.4, cD, 0.8); g.beginPath(); g.moveTo(x1, y1 + 1.5); g.lineTo(x2, y2 + 1.5); g.strokePath();
    }
    // zapatas de goma en la base
    g.fillStyle(0x2b2b30, 1); g.fillEllipse(-26, 4, 7, 4); g.fillEllipse(-12, 10, 7, 4);
  });
}

export function fallenTool(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // radial/amoladora tirada en el suelo (riesgo de tropiezo)
  return container(scene, (g) => {
    contact(g, 26, 9);
    // cable suelto
    g.lineStyle(2, 0x2b2b30, 0.9);
    g.beginPath(); g.moveTo(-26, 5); g.lineTo(-17, -1); g.lineTo(-23, -5); g.lineTo(-14, -8); g.strokePath();
    // cuerpo motor (naranja) con rejillas
    g.fillStyle(0xc9560f, 1); g.fillRoundedRect(-20, -12, 28, 14, 5);
    g.fillStyle(0xe86a1e, 1); g.fillRoundedRect(-20, -12, 28, 7, 5);
    g.fillStyle(0x2b2b30, 1); for (let i = 0; i < 3; i++) g.fillRect(-8 + i * 5, -11, 2.5, 5);
    // empuñadura lateral
    g.fillStyle(0x2b2b30, 1); g.fillRect(-9, -17, 4, 7);
    // guarda + disco de corte
    g.fillStyle(0x5a5f68, 1); g.beginPath(); g.arc(13, -3, 15, Math.PI * 1.05, Math.PI * 2.15, false); g.closePath(); g.fillPath();
    g.fillStyle(0x8a8a90, 1); g.fillCircle(13, -3, 12);
    g.lineStyle(1.3, 0x6a6a70, 0.85); g.strokeCircle(13, -3, 9); g.strokeCircle(13, -3, 5);
    g.fillStyle(0x3a3f47, 1); g.fillCircle(13, -3, 2.5);
  }, true, 26, 9);
}

export function helmet(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // casco de obra en el suelo (abollado): ala, cúpula, cresta, ventilación
  return container(scene, (g) => {
    contact(g, 22, 8);
    // ala
    g.fillStyle(0xc9a10a, 1); g.fillEllipse(0, 2, 46, 17);
    g.fillStyle(0xd8b21a, 1); g.fillEllipse(0, 1, 40, 13);
    // cúpula
    g.fillStyle(0xf0c400, 1); g.beginPath(); g.arc(0, 2, 20, Math.PI, 0, false); g.closePath(); g.fillPath();
    g.fillStyle(0xf5cf3a, 1); g.beginPath(); g.arc(-3, 2, 17, Math.PI * 1.05, Math.PI * 1.75, false); g.closePath(); g.fillPath(); // brillo
    // cresta central + ranuras de ventilación
    g.lineStyle(2, 0xc9a10a, 1); g.beginPath(); g.moveTo(0, -18); g.lineTo(0, 2); g.strokePath();
    g.lineStyle(1.4, 0xc9a10a, 0.8);
    g.beginPath(); g.moveTo(-11, -8); g.lineTo(-11, 0); g.moveTo(11, -8); g.lineTo(11, 0); g.strokePath();
    g.lineStyle(2, 0xa8850a, 1); g.beginPath(); g.arc(0, 2, 20, Math.PI, 0, false); g.strokePath();
  }, true, 22, 8);
}

export function locker(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // taquilla / armario de seguridad: dos puertas, rejillas, manillas, patas, señal EPI
  return container(scene, (g) => {
    contact(g, 30, 10);
    isoBox(g, 30, 20, 80, 0x4f6b8a, { stroke: 0x2c3f52 });
    // puertas
    g.fillStyle(0x3d556f, 1); g.fillRect(-24, -76, 23, 68); g.fillRect(1, -76, 23, 68);
    g.lineStyle(1.4, 0x2c3f52, 0.9); g.strokeRect(-24, -76, 23, 68); g.strokeRect(1, -76, 23, 68);
    // rejillas de ventilación (arriba y abajo de cada puerta)
    g.fillStyle(0x2c3f52, 0.85);
    for (const dx of [-12.5, 12.5]) for (let i = 0; i < 3; i++) { g.fillRect(dx - 8, -71 + i * 4, 16, 1.6); g.fillRect(dx - 8, -22 + i * 4, 16, 1.6); }
    // manillas verticales
    g.fillStyle(0xcfd8e2, 1); g.fillRect(-5, -48, 3, 13); g.fillRect(4, -48, 3, 13);
    // patas
    g.fillStyle(0x2c3f52, 1); g.fillRect(-22, -8, 5, 8); g.fillRect(17, -8, 5, 8);
    // brillo del canto
    hiLine(g, -24, -74, -24, -10, 0xffffff, 0.16, 2);
    // señal EPI obligatorio (azul) en la parte alta
    g.fillStyle(0x1f6fbf, 1); g.fillCircle(0, -88, 10);
    g.fillStyle(0xffffff, 1); g.fillCircle(0, -92, 3.4); g.fillRect(-3.4, -88, 6.8, 7);
  });
}

export function hut(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // caseta de obra (contenedor de oficina): chapa, ventana, puerta, cartel.
  // Puerta y ventana se PROYECTAN sobre sus caras isométricas (facePanel).
  const HW = 56, HD = 40, PH = 58;
  return container(scene, (g) => {
    contact(g, 56, 16);
    isoBox(g, HW, HD, PH, 0x9ab0a2, { stroke: 0x5d7365 });
    // techo plano con reborde
    g.fillStyle(0x8aa093, 1);
    g.beginPath(); g.moveTo(0, -100); g.lineTo(56, -60); g.lineTo(0, -20); g.lineTo(-56, -60); g.closePath(); g.fillPath();
    g.lineStyle(2, 0x5d7365, 0.8); g.strokePath();

    // --- cara SO (frontal-izquierda): chapa acanalada + puerta + cartel ---
    for (let i = 1; i < 9; i++) { const u = i / 9; faceLine(g, "SO", HW, HD, PH, u, 0, u, 1, 0x7d9384, 1, 0.5); }
    // cartel "oficina" (arriba, junto a la puerta)
    facePanel(g, "SO", HW, HD, PH, 0.30, 0.66, 0.05, 0.15, 0xf0a91b);
    // puerta
    facePanel(g, "SO", HW, HD, PH, 0.28, 0.70, 0.20, 0.97, 0x4a5f52, { stroke: 0x35473c });
    facePanel(g, "SO", HW, HD, PH, 0.30, 0.32, 0.22, 0.95, 0x3c4e43); // junta central
    facePanel(g, "SO", HW, HD, PH, 0.62, 0.66, 0.55, 0.63, 0xcfd8e2); // manilla

    // --- cara SE (frontal-derecha): ventana con marco, cristal y junquillos ---
    facePanel(g, "SE", HW, HD, PH, 0.22, 0.74, 0.14, 0.52, 0x2c3f52); // marco
    facePanel(g, "SE", HW, HD, PH, 0.26, 0.70, 0.18, 0.48, 0xbfe0e6); // cristal
    facePanel(g, "SE", HW, HD, PH, 0.26, 0.47, 0.18, 0.32, 0xdff1f4, { alpha: 0.55 }); // reflejo
    faceLine(g, "SE", HW, HD, PH, 0.48, 0.18, 0.48, 0.48, 0x2c3f52, 1.5); // montante vertical
    faceLine(g, "SE", HW, HD, PH, 0.26, 0.33, 0.70, 0.33, 0x2c3f52, 1.5); // travesaño horizontal
  });
}

/** Caja de herramientas metálica. */
export function toolbox(scene: Phaser.Scene, color = 0xc23a2a): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 22, 9);
    isoBox(g, 22, 12, 14, color, { stroke: darken(color, 0.6) });
    g.lineStyle(1.5, darken(color, 0.55), 0.9); g.beginPath(); g.moveTo(-22, -14); g.lineTo(0, -2); g.lineTo(22, -14); g.strokePath();
    g.lineStyle(3, 0x2a2a30, 1); g.beginPath(); g.arc(0, -22, 8, Math.PI, 0); g.strokePath();
    g.fillStyle(0xd8dde2, 1); g.fillRect(-10, -13, 3, 5); g.fillRect(7, -13, 3, 5);
    hiLine(g, -22, -14, 0, -26);
  });
}

/** Estantería industrial con cajas. */
export function shelving(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 40, 15);
    isoBox(g, 40, 18, 74, 0x6f747b, { stroke: 0x4a4f57 });
    g.lineStyle(2, 0x3a3f47, 0.7);
    for (const h of [26, 50]) { g.beginPath(); g.moveTo(-40, -h); g.lineTo(0, -h + 18); g.lineTo(40, -h); g.strokePath(); }
    // cajas en las baldas
    isoBox(g, 15, 9, 14, 0xcaa46a, { stroke: 0x9a7a48, oy: -50, ox: -16 });
    isoBox(g, 13, 8, 12, 0xb0754a, { stroke: 0x854f30, oy: -50, ox: 14 });
    isoBox(g, 16, 9, 14, 0xd8c6a0, { stroke: 0xa89a78, oy: -26, ox: -8 });
    isoBox(g, 12, 7, 11, 0x8fa3b0, { stroke: 0x5f7380, oy: -26, ox: 18 });
  });
}

/** Manguera/cable enrollado en el suelo. */
export function hosecoil(scene: Phaser.Scene, color = 0x1f6b3a): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 26, 11);
    for (let i = 3; i >= 0; i--) { g.lineStyle(5, i % 2 ? darken(color, 0.7) : color, 1); g.strokeEllipse(0, -4, 12 + i * 7, 6 + i * 3.2); }
  }, false);
}

/** Botellero de gases (3 cilindros encadenados a un bastidor). */
export function gasrack(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 34, 13);
    g.fillStyle(0x5a5f68, 1); g.fillRect(-30, -8, 60, 8);
    const cyl = (x: number, c: number): void => {
      g.fillStyle(c, 1); g.fillRoundedRect(x - 7, -58, 14, 52, 7);
      g.fillStyle(darken(c, 0.72), 1); g.fillRoundedRect(x + 2, -58, 5, 52, 3);
      g.fillStyle(0x9a9aa2, 1); g.fillRoundedRect(x - 4, -66, 8, 10, 3);
    };
    cyl(-18, 0x2f6b8f); cyl(0, 0xb23a2a); cyl(18, 0xd8a800);
    g.lineStyle(2, 0x8a8f96, 1); g.beginPath(); g.moveTo(-26, -42); g.lineTo(26, -42); g.strokePath();
  });
}

/**
 * Luminaria industrial colgante (cadena + pantalla cónica). Se coloca en alto
 * (el foco de luz en el suelo se dibuja aparte, a la profundidad del suelo).
 */
export function hanglamp(scene: Phaser.Scene, drop = 70): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    g.lineStyle(2, 0x3a3f47, 1); g.beginPath(); g.moveTo(0, -drop); g.lineTo(0, -drop + 26); g.strokePath();
    g.fillStyle(0x33383f, 1); g.beginPath(); g.moveTo(-16, -drop + 42); g.lineTo(16, -drop + 42); g.lineTo(9, -drop + 26); g.lineTo(-9, -drop + 26); g.closePath(); g.fillPath();
    g.fillStyle(0x4a5058, 1); g.fillEllipse(0, -drop + 42, 16, 5);
    g.fillStyle(0xfff4c8, 1); g.fillEllipse(0, -drop + 43, 9, 3);
    g.fillStyle(0xfff4c8, 0.18); g.fillTriangle(-13, -drop + 44, 13, -drop + 44, 0, -drop + 92);
  }, false);
}

/** Campana de extracción de laboratorio (hueco de trabajo con guillotina de cristal). */
export function fumehood(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const HW = 38, HD = 20, PH = 82;
  return container(scene, (g) => {
    contact(g, 38, 15);
    isoBox(g, HW, HD, PH, 0xdfe4e2, { stroke: 0xa8b0ac });
    g.fillStyle(0xb0b6bb, 1); g.fillRect(-6, -PH - 24, 12, 26); // conducto de extracción
    g.fillStyle(0x8a938e, 1); g.fillRect(-9, -PH - 24, 3, 26);
    facePanel(g, "SE", HW, HD, PH, 0.14, 0.86, 0.2, 0.64, 0x243434);            // hueco de trabajo
    facePanel(g, "SE", HW, HD, PH, 0.14, 0.86, 0.2, 0.36, 0xbfe0e6, { alpha: 0.5 }); // guillotina de cristal
    faceLine(g, "SE", HW, HD, PH, 0.14, 0.36, 0.86, 0.36, 0x6a8a86, 2);
    // mandos
    g.fillStyle(0x2f8f57, 1); g.fillCircle(-22, -26, 2.5); g.fillStyle(0xb23a2a, 1); g.fillCircle(-15, -26, 2.5);
  });
}

/** Estantería de reactivos (botellas de colores en dos baldas). */
export function reagentShelf(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 34, 13);
    isoBox(g, 34, 16, 44, 0xcdd3d0, { stroke: 0x9aa39e });
    g.lineStyle(1.5, 0x9aa39e, 0.7); g.beginPath(); g.moveTo(-34, -24); g.lineTo(0, -8); g.lineTo(34, -24); g.strokePath();
    const cols = [0x3a7a3a, 0xd3651a, 0x2c6270, 0xc9a20a, 0xb23a2a, 0x6a4a9a];
    for (let i = 0; i < 6; i++) { const bx = -25 + i * 10; g.fillStyle(cols[i], 1); g.fillRoundedRect(bx - 3, -46, 6, 13, 2); g.fillStyle(0xffffff, 0.25); g.fillRect(bx - 3, -46, 2, 13); }
    for (let i = 0; i < 5; i++) { const bx = -20 + i * 10; g.fillStyle(cols[(i + 2) % 6], 1); g.fillRoundedRect(bx - 3, -26, 6, 13, 2); }
  });
}

/** Fregadero de laboratorio con grifo. */
export function labsink(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 28, 12);
    isoBox(g, 28, 15, 30, 0xe0e4e2, { stroke: 0xa8b0ac });
    g.fillStyle(0x9aa39e, 1); g.beginPath(); g.moveTo(-12, -30); g.lineTo(0, -24); g.lineTo(12, -30); g.lineTo(0, -36); g.closePath(); g.fillPath();
    g.fillStyle(0x7c847f, 1); g.fillEllipse(0, -30, 12, 5);
    g.lineStyle(3, 0xb0b6bb, 1); g.beginPath(); g.moveTo(-2, -30); g.lineTo(-2, -46); g.lineTo(4, -46); g.strokePath();
  });
}

/** Contenedor de residuos (tapa y etiqueta). */
export function wastebin(scene: Phaser.Scene, color = 0xd0a000): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 15, 8);
    isoBox(g, 15, 10, 34, color, { stroke: darken(color, 0.6) });
    g.fillStyle(darken(color, 0.82), 1); g.beginPath(); g.moveTo(-15, -34); g.lineTo(0, -28); g.lineTo(15, -34); g.lineTo(0, -40); g.closePath(); g.fillPath();
    g.fillStyle(0xf4f4ee, 0.9); g.fillRect(-7, -24, 14, 11); g.fillStyle(darken(color, 0.5), 1); g.fillRect(-5, -21, 10, 2); g.fillRect(-5, -18, 7, 2);
  });
}

/** Luminaria de techo fluorescente (carcasa + difusor con rejilla + tirantes). */
export function ceilingPanel(scene: Phaser.Scene, drop = 150): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    const w = 42, h = 21, cy = -drop;
    // tirantes de suspensión (cortos, hacia el techo)
    g.lineStyle(1.5, 0x2a2f36, 0.85);
    g.beginPath(); g.moveTo(-w * 0.5, cy - h * 0.5 - 2); g.lineTo(-w * 0.5, cy - h * 0.5 - 15); g.moveTo(w * 0.5, cy - h * 0.5 - 2); g.lineTo(w * 0.5, cy - h * 0.5 - 15); g.strokePath();
    // carcasa metálica (rombo exterior)
    g.fillStyle(0x4a5058, 1); g.beginPath(); g.moveTo(0, cy - h - 5); g.lineTo(w + 5, cy); g.lineTo(0, cy + h + 5); g.lineTo(-w - 5, cy); g.closePath(); g.fillPath();
    // difusor iluminado (rombo interior, blanco cálido tenue)
    g.fillStyle(0xeef0e0, 0.98); g.beginPath(); g.moveTo(0, cy - h); g.lineTo(w, cy); g.lineTo(0, cy + h); g.lineTo(-w, cy); g.closePath(); g.fillPath();
    // rejilla del difusor (dos tubos)
    g.lineStyle(1.5, 0xb8bcae, 0.6); g.beginPath(); g.moveTo(-w * 0.55, cy - h * 0.1); g.lineTo(w * 0.45, cy + h * 0.4); g.moveTo(-w * 0.45, cy - h * 0.4); g.lineTo(w * 0.55, cy + h * 0.1); g.strokePath();
    // halo de luz
    g.fillStyle(0xeef0e0, 0.06); g.fillEllipse(0, cy + 74, 76, 35);
  }, false);
}

/** Archivador metálico de oficina (cajones en la cara frontal). */
export function filecabinet(scene: Phaser.Scene, color = 0x8a8f96): Phaser.GameObjects.Container {
  const HW = 20, HD = 13, PH = 54;
  return container(scene, (g) => {
    contact(g, 20, 10);
    isoBox(g, HW, HD, PH, color, { stroke: darken(color, 0.7) });
    for (let i = 0; i < 3; i++) {
      const v0 = 0.12 + i * 0.28;
      facePanel(g, "SE", HW, HD, PH, 0.18, 0.86, v0, v0 + 0.2, darken(color, 0.9), { stroke: darken(color, 0.6) });
      facePanel(g, "SE", HW, HD, PH, 0.42, 0.62, v0 + 0.07, v0 + 0.12, darken(color, 0.55)); // tirador
    }
  });
}

/** Dispensador de agua con garrafa. */
export function watercooler(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 13, 8);
    isoBox(g, 13, 9, 34, 0xe4e8ea, { stroke: 0xb0b6bb });
    g.fillStyle(0x3a8fc4, 0.85); g.beginPath(); g.moveTo(-9, -34); g.lineTo(9, -34); g.lineTo(6, -58); g.lineTo(-6, -58); g.closePath(); g.fillPath();
    g.fillStyle(0xbfe0e6, 0.4); g.fillRect(-8, -50, 4, 14);
    g.fillStyle(0x2a6f9a, 1); g.fillRect(-5, -58, 10, 4);
    g.fillStyle(0x3a4048, 1); g.fillRect(-3, -30, 2, 4); g.fillStyle(0xb23a2a, 1); g.fillRect(1, -30, 2, 4);
  });
}

/** Impresora / multifunción de oficina. */
export function printer(scene: Phaser.Scene): Phaser.GameObjects.Container {
  const HW = 20, HD = 14, PH = 20;
  return container(scene, (g) => {
    contact(g, 20, 10);
    isoBox(g, HW, HD, PH, 0x4a5058, { stroke: 0x2f343a });
    g.fillStyle(0xf0f0ea, 1); g.fillRect(-8, -PH - 5, 16, 6);
    g.fillStyle(0x2f8f57, 1); g.fillCircle(-11, -14, 2); g.fillStyle(0x8fa3b0, 1); g.fillRect(-7, -16, 11, 5);
  });
}

export function extinguisher(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // extintor sobre soporte de pared
  return container(scene, (g) => {
    contact(g, 14, 7);
    // panel/soporte
    g.fillStyle(0x7a1f16, 0.2); g.fillRoundedRect(-16, -62, 32, 62, 3);
    g.fillStyle(0xc9c9cf, 1); g.fillRect(-12, -30, 24, 4); // fleje soporte
    // cuerpo rojo con brillo
    g.fillStyle(0x8a2318, 1); g.fillRoundedRect(-9, -40, 18, 40, 6);
    g.fillStyle(0xc0392b, 1); g.fillRoundedRect(-9, -40, 12, 40, 6);
    g.fillStyle(0xd85a4a, 0.6); g.fillRect(-6, -38, 4, 36);
    // cabezal + maneta + manómetro
    g.fillStyle(0x2b2b30, 1); g.fillRoundedRect(-6, -50, 12, 12, 3);
    g.fillStyle(0x3a3f47, 1); g.fillRect(-10, -49, 6, 4); // maneta
    g.fillStyle(0x2f8f57, 1); g.fillCircle(6, -45, 3); g.fillStyle(0x0f4a2c, 1); g.fillCircle(6, -45, 1.2); // manómetro
    // manguera
    g.lineStyle(2.5, 0x2b2b30, 1); g.beginPath(); g.moveTo(-8, -44); g.lineTo(-14, -30); g.lineTo(-10, -20); g.strokePath();
    // etiqueta
    g.fillStyle(0xf4ead0, 1); g.fillRect(-7, -28, 14, 12);
    g.fillStyle(0xb23a2a, 0.5); g.fillRect(-7, -28, 14, 3);
  }, true, 14, 7);
}

export function mixer(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // hormigonera de obra: chasis + ruedas + motor + tambor basculante
  return container(scene, (g) => {
    contact(g, 34, 12);
    // --- chasis / bastidor metálico ---
    g.lineStyle(5, 0x4a4f57, 1);
    g.beginPath(); g.moveTo(-24, 0); g.lineTo(0, -26); g.lineTo(24, 0); g.strokePath(); // A-frame
    g.lineStyle(4, 0x5a5f68, 1);
    g.beginPath(); g.moveTo(-24, 0); g.lineTo(24, 0); g.strokePath(); // eje inferior
    g.lineStyle(3, 0x3a3f47, 1);
    g.beginPath(); g.moveTo(0, -26); g.lineTo(0, -6); g.strokePath();
    // --- ruedas (goma + llanta) ---
    for (const wx of [-24, 24]) {
      g.fillStyle(0x000000, 0.18); g.fillEllipse(wx, 4, 18, 7);
      g.fillStyle(0x24242a, 1); g.fillCircle(wx, 0, 8);
      g.fillStyle(0x3a3a42, 1); g.fillCircle(wx, 0, 4.5);
      g.fillStyle(0x6a6a72, 1); g.fillCircle(wx, 0, 2);
    }
    // --- motor / caja de engranajes (detrás-izquierda) ---
    isoBox(g, 12, 8, 14, 0x37527a, { stroke: 0x24354f, ox: -20, oy: -20 });
    rivets(g, [[-27, -30], [-13, -30], [-27, -20], [-13, -20]], 0x1f2f47, 1.2);
    // --- tambor (cuerpo naranja) ---
    g.fillStyle(0xd98c14, 1); g.fillEllipse(2, -40, 48, 42);          // cara sombreada
    g.fillStyle(0xf0a91b, 1); g.fillEllipse(-2, -44, 46, 40);          // cuerpo iluminado
    // aros/refuerzos del tambor
    g.lineStyle(2.5, 0xc9830a, 0.9);
    g.strokeEllipse(-2, -44, 46, 40);
    g.lineStyle(1.6, 0xc9830a, 0.7);
    g.beginPath(); g.arc(-2, -44, 30, -1.9, 1.2, false); g.strokePath();
    // boca del tambor (hueco oscuro con reborde)
    g.fillStyle(0x8a5a09, 1); g.fillEllipse(16, -46, 22, 24);
    g.fillStyle(0x3a2606, 1); g.fillEllipse(17, -46, 14, 17);
    g.lineStyle(2, 0xf0c24a, 0.8); g.strokeEllipse(16, -46, 22, 24);
    // remaches del tambor + brillo
    rivets(g, [[-24, -44], [-16, -30], [-16, -58], [-2, -26]], 0xa56a0a, 1.3);
    hiLine(g, -28, -50, -10, -60, 0xffffff, 0.3, 2);
    // volante/rueda de basculación
    g.lineStyle(3, 0x2a2a30, 1); g.strokeCircle(-22, -40, 7);
    g.lineStyle(2, 0x2a2a30, 1);
    g.beginPath(); g.moveTo(-29, -40); g.lineTo(-15, -40); g.moveTo(-22, -47); g.lineTo(-22, -33); g.strokePath();
  }, true, 34, 13);
}

export function wheelbarrow(scene: Phaser.Scene): Phaser.GameObjects.Container {
  // carretilla con cubeta, rueda, patas y carga
  return container(scene, (g) => {
    contact(g, 30, 11);
    // patas + chasis
    g.lineStyle(4, 0x3a3f47, 1);
    g.beginPath(); g.moveTo(-24, -12); g.lineTo(-18, 2); g.moveTo(6, -14); g.lineTo(2, 2); g.strokePath();
    g.beginPath(); g.moveTo(22, -16); g.lineTo(40, 2); g.strokePath(); // brazos/mangos
    g.fillStyle(0x6a4f2c, 1); g.fillCircle(41, 2, 3); g.fillCircle(41, -2, 3); // empuñaduras
    // cubeta (naranja) con interior
    g.fillStyle(0xc9560f, 1); g.fillEllipse(-2, -14, 60, 26);
    g.fillStyle(0xe86a1e, 1); g.beginPath(); g.arc(-2, -14, 30, Math.PI, 0, false); g.closePath(); g.fillPath();
    g.fillStyle(0x8a5a09, 1); g.fillEllipse(-2, -18, 52, 16); // hueco interior
    // carga (arena/escombro)
    g.fillStyle(0xcdbf9c, 1); g.fillEllipse(-2, -20, 46, 12);
    g.fillStyle(0xbdb08c, 1); g.fillEllipse(-10, -21, 16, 6); g.fillEllipse(8, -20, 14, 5);
    // rueda
    g.fillStyle(0x241d14, 1); g.fillCircle(-22, 3, 9);
    g.fillStyle(0x4a4f57, 1); g.fillCircle(-22, 3, 4);
    g.fillStyle(0x8a8f98, 1); g.fillCircle(-22, 3, 1.6);
  }, true, 30, 11);
}

export function scaffold(scene: Phaser.Scene, floors = 2): Phaser.GameObjects.Container {
  // andamio metálico galvanizado: postes, tablones de madera, rodapié, barandilla, cruces
  return container(scene, (g) => {
    contact(g, 54, 12);
    const pole = 0x9aa2a9, poleD = 0x5f676d;
    const fh = 74;
    const H = fh * floors + 16;
    const posts: [number, number][] = [[-52, 12], [52, 12], [0, -18], [0, 42]];
    // placas base
    g.fillStyle(0x3a3f47, 1);
    for (const [px] of posts) g.fillEllipse(px, 3, 14, 6);
    // postes (lado iluminado + lado sombra)
    g.lineStyle(6, pole, 1);
    for (const [px, py] of posts) { g.beginPath(); g.moveTo(px, py); g.lineTo(px, py - H); g.strokePath(); }
    g.lineStyle(2, poleD, 0.6);
    for (const [px, py] of posts) { g.beginPath(); g.moveTo(px + 2, py); g.lineTo(px + 2, py - H); g.strokePath(); }
    // cruces diagonales entre plantas (cara frontal)
    g.lineStyle(2.5, poleD, 0.9);
    for (let f = 0; f < floors; f++) {
      const y0 = -f * fh + 12, y1 = -(f + 1) * fh + 12;
      g.beginPath(); g.moveTo(-52, y0); g.lineTo(0, y1 + 30); g.moveTo(52, y0); g.lineTo(0, y1 + 30); g.strokePath();
      g.beginPath(); g.moveTo(-52, y1); g.lineTo(0, y0 + 30); g.moveTo(52, y1); g.lineTo(0, y0 + 30); g.strokePath();
    }
    // por planta: deck de tablones + rodapié + barandilla doble
    for (let f = 1; f <= floors; f++) {
      const y = -f * fh + 12;
      g.fillStyle(0xbb9450, 1);
      g.beginPath(); g.moveTo(0, y - 30); g.lineTo(52, y); g.lineTo(0, y + 30); g.lineTo(-52, y); g.closePath(); g.fillPath();
      g.lineStyle(1.5, 0x8a6f45, 0.7);
      for (let t = -2; t <= 2; t++) { g.beginPath(); g.moveTo(-40 + t * 8, y + 10 - t * 3); g.lineTo(40 + t * 8, y - 10 - t * 3); g.strokePath(); }
      // rodapié (toe board)
      g.fillStyle(0x9a7a3a, 1);
      g.beginPath(); g.moveTo(-52, y); g.lineTo(0, y + 30); g.lineTo(0, y + 25); g.lineTo(-52, y - 5); g.closePath(); g.fillPath();
      // barandilla metálica (dos alturas)
      g.lineStyle(3, pole, 1);
      g.beginPath(); g.moveTo(-52, y - 24); g.lineTo(0, y - 48); g.lineTo(52, y - 24); g.strokePath();
      g.beginPath(); g.moveTo(-52, y - 12); g.lineTo(0, y - 36); g.lineTo(52, y - 12); g.strokePath();
    }
  });
}

/** Máquina industrial genérica (fábrica/labo/central): panel, seta, palanca. */
export function machine(scene: Phaser.Scene, color = 0x6a737a): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 30, 11);
    isoBox(g, 28, 20, 46, color, { stroke: darken(color, 0.6) });
    // panel de control
    g.fillStyle(0x2c3138, 1); g.fillRect(-24, -42, 20, 22);
    g.fillStyle(0x8ac6d0, 0.85); g.fillRect(-22, -40, 16, 11);
    g.fillStyle(0x2f8f57, 1); g.fillCircle(-20, -25, 2.4); g.fillStyle(0xd8b93f, 1); g.fillCircle(-13, -25, 2.4);
    // seta de emergencia
    g.fillStyle(0xb23a2a, 1); g.fillCircle(8, -42, 5.5); g.fillStyle(0x8a1f14, 1); g.fillCircle(8, -42, 2.6);
    // palanca
    g.lineStyle(3, 0x3a3f47, 1); g.beginPath(); g.moveTo(16, -30); g.lineTo(23, -44); g.strokePath();
    g.fillStyle(0xd8b93f, 1); g.fillCircle(23, -44, 3);
    // franja de peligro en la base
    g.fillStyle(0x2b2b30, 0.85); for (let i = -1; i <= 1; i++) g.fillRect(-22 + i * 16, -10, 9, 4);
    g.fillStyle(0xd8b93f, 0.9); for (let i = -1; i <= 1; i++) g.fillRect(-16 + i * 16, -10, 9, 4);
    hiLine(g, -26, -46, -4, -36, 0xffffff, 0.14, 2);
  });
}

/** Portón/salida de recinto con cartel verde. */
export function gate(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 32, 12);
    g.fillStyle(0x5a5f68, 1); g.fillRect(-30, -56, 6, 56); g.fillRect(24, -56, 6, 56);
    g.fillStyle(0x3a3f47, 1); g.fillRect(-30, -56, 2, 56); g.fillRect(24, -56, 2, 56);
    g.fillStyle(0xd8b93f, 1); g.fillRect(-27, -54, 51, 6);
    g.lineStyle(3, 0xd8b93f, 1); g.strokeRect(-27, -54, 51, 50);
    g.lineStyle(1.4, 0xb99a28, 0.9);
    for (let x = -24; x < 24; x += 9) { g.beginPath(); g.moveTo(x, -54); g.lineTo(x - 7, -4); g.strokePath(); g.beginPath(); g.moveTo(x, -4); g.lineTo(x - 7, -54); g.strokePath(); }
    g.fillStyle(0xb23a2a, 0.85); for (let x = -25; x < 24; x += 14) g.fillRect(x, -53, 7, 4);
    g.fillStyle(0x2f8f57, 1); g.fillRoundedRect(-16, -74, 32, 13, 2);
    g.fillStyle(0xffffff, 1); g.fillRect(-10, -69, 10, 3); g.fillTriangle(0, -72, 0, -60, 8, -66);
  }, false);
}

/** Persona de oficina (NPC sin casco): camisa, corbata, identificador. */
export function officeperson(scene: Phaser.Scene, opts: { shirt?: number; hair?: number; skin?: number } = {}): Phaser.GameObjects.Container {
  const shirt = opts.shirt ?? 0x3a5a8a;
  const hair = opts.hair ?? 0x3a2c1e;
  const skin = opts.skin ?? 0xe8b48c;
  return container(scene, (g) => {
    contact(g, 20, 9);
    g.fillStyle(0x2b3550, 1); g.fillRect(-11, -22, 9, 22); g.fillRect(2, -22, 9, 22);
    g.fillStyle(0x212a42, 1); g.fillRect(4, -22, 7, 22);
    g.fillStyle(0x241d14, 1); g.fillEllipse(-6, -1, 13, 6); g.fillEllipse(6, -1, 13, 6);
    // brazos (mangas de camisa) + manos
    g.fillStyle(shirt, 1); g.fillRect(-18, -40, 7, 22); g.fillRect(11, -40, 7, 22);
    g.fillStyle(skin, 1); g.fillCircle(-15, -20, 4); g.fillCircle(15, -20, 4);
    // torso camisa + corbata + identificador
    g.fillStyle(shirt, 1); g.fillRect(-13, -42, 26, 26);
    g.fillStyle(darken(shirt, 0.82), 1); g.fillRect(3, -42, 10, 26);
    g.fillStyle(0xf4f4f4, 1); g.fillRect(-4, -42, 8, 12);
    g.fillStyle(0xb23a2a, 1); g.fillTriangle(0, -40, -3, -37, 3, -37); g.fillRect(-2, -37, 4, 13);
    g.fillStyle(0x9aa0a8, 1); g.fillRect(3, -42, 2, 8); g.fillStyle(0xf4ead0, 1); g.fillRect(1, -34, 7, 6);
    // cuello + cabeza + pelo + ojos
    g.fillStyle(0xdca77e, 1); g.fillRect(-4, -46, 8, 6);
    g.fillStyle(skin, 1); g.fillCircle(0, -52, 11);
    g.fillStyle(hair, 1); g.beginPath(); g.arc(0, -54, 11, Math.PI, 0, false); g.closePath(); g.fillPath();
    g.fillStyle(0x241d14, 1); g.fillCircle(-4, -52, 1.6); g.fillCircle(4, -52, 1.6);
  }, true, 20, 9);
}

/** Mesa de oficina con monitor, teclado, papeles y taza. */
export function desk(scene: Phaser.Scene, top = 0x9a7a52): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 42, 14);
    isoBox(g, 38, 22, 24, top, { stroke: darken(top, 0.62) });
    // monitor
    g.fillStyle(0x2b2f35, 1); g.fillRect(-4, -52, 26, 18);
    g.fillStyle(0x8ac6d0, 0.85); g.fillRect(-2, -50, 22, 14);
    g.fillStyle(0xdff1f4, 0.5); g.fillRect(-2, -50, 10, 6);
    g.fillStyle(0x2b2f35, 1); g.fillRect(6, -34, 10, 5); g.fillRect(8, -30, 6, 6);
    // teclado + papeles + taza
    g.fillStyle(0x3a3f47, 1); g.fillRect(-24, -27, 20, 7);
    g.fillStyle(0xf4ead0, 1); g.fillRect(-22, -34, 12, 9);
    g.lineStyle(1, 0x9a8f7c, 0.7); for (let i = 0; i < 3; i++) { g.beginPath(); g.moveTo(-20, -31 + i * 2); g.lineTo(-12, -31 + i * 2); g.strokePath(); }
    g.fillStyle(0xb23a2a, 1); g.fillRect(16, -29, 6, 7);
    hiLine(g, -36, -26, -2, -14, 0xffffff, 0.12, 2);
  });
}

/** Silla de oficina (giratoria con ruedas). */
export function officechair(scene: Phaser.Scene, color = 0x2c6270): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 16, 8);
    g.lineStyle(3, 0x3a3f47, 1);
    for (const [dx, dy] of [[-14, 4], [14, 4], [0, 9], [-10, -2], [10, -2]] as [number, number][]) { g.beginPath(); g.moveTo(0, -14); g.lineTo(dx, dy); g.strokePath(); g.fillStyle(0x2b2b30, 1); g.fillCircle(dx, dy, 2.5); }
    g.fillStyle(0x6a6f77, 1); g.fillRect(-2, -30, 4, 16);
    g.fillStyle(color, 1); g.fillEllipse(0, -30, 28, 12);
    g.fillStyle(darken(color, 0.85), 1); g.fillRoundedRect(-11, -52, 22, 22, 5);
    g.fillStyle(Math.min(0xffffff, darken(color, 1.15)) & 0xffffff, 0.4); g.fillRect(-9, -50, 6, 18);
  });
}

/** Planta de interior en maceta. */
export function plant(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 12, 6);
    g.fillStyle(0x9a5a3a, 1); g.beginPath(); g.moveTo(-10, -2); g.lineTo(10, -2); g.lineTo(8, -18); g.lineTo(-8, -18); g.closePath(); g.fillPath();
    g.fillStyle(0x7a4530, 1); g.beginPath(); g.moveTo(2, -2); g.lineTo(10, -2); g.lineTo(8, -18); g.lineTo(3, -18); g.closePath(); g.fillPath();
    g.fillStyle(0x357033, 1); g.fillCircle(0, -30, 12); g.fillCircle(-9, -24, 9); g.fillCircle(9, -24, 9); g.fillCircle(0, -40, 10);
    g.fillStyle(0x4a8a42, 1); g.fillCircle(-4, -34, 7); g.fillCircle(5, -30, 6);
  });
}

/** Transformador de subestación (central eléctrica): radiador, aisladores, riesgo. */
export function transformer(scene: Phaser.Scene, color = 0x8a9299): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 32, 12);
    isoBox(g, 28, 18, 42, color, { stroke: darken(color, 0.6) });
    // aletas de radiador
    g.lineStyle(1.5, darken(color, 0.72), 0.7);
    for (let i = 0; i < 6; i++) { const x = 3 + i * 4; g.beginPath(); g.moveTo(x, -40); g.lineTo(x, -8); g.strokePath(); }
    // aisladores (bushings) arriba
    for (const bx of [-14, 0, 14]) {
      g.fillStyle(0x3a3f47, 1); g.fillRect(bx - 2, -54, 4, 12);
      g.fillStyle(0xd8a24a, 1); for (let k = 0; k < 3; k++) g.fillEllipse(bx, -54 - k * 5, 10 - k * 2, 4);
    }
    // señal de riesgo eléctrico
    g.fillStyle(0xf0c400, 1); g.fillTriangle(-14, -18, -24, -18, -19, -32);
    g.fillStyle(0x241d14, 1); g.fillRect(-20, -28, 2, 6); g.fillRect(-18, -25, 4, 2);
    hiLine(g, -26, -46, -4, -36, 0xffffff, 0.14, 2);
  });
}

/** Botella de gas comprimido (labo/central). */
export function gascylinder(scene: Phaser.Scene, color = 0x2c6270): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 14, 7);
    g.fillStyle(darken(color, 0.6), 1); g.fillEllipse(0, -2, 26, 12);
    g.fillStyle(color, 1); g.fillRect(-12, -58, 24, 56);
    g.fillStyle(Math.min(0xffffff, darken(color, 1.16)) & 0xffffff, 0.5); g.fillRect(-10, -56, 6, 52);
    g.fillStyle(darken(color, 0.85), 1); g.fillEllipse(0, -58, 24, 13);
    g.fillStyle(color, 1); g.beginPath(); g.arc(0, -58, 12, Math.PI, 0, false); g.closePath(); g.fillPath();
    // cuello + válvula
    g.fillStyle(0x8a9299, 1); g.fillRect(-4, -72, 8, 14);
    g.fillStyle(0xb23a2a, 1); g.fillRect(-7, -74, 14, 4);
    // etiqueta + aro
    g.fillStyle(0xf4ead0, 0.9); g.fillRect(-9, -42, 18, 12);
    g.lineStyle(2, 0x6a6f77, 0.8); g.strokeEllipse(0, -20, 26, 12);
  }, true, 14, 7);
}

/** Mesa de laboratorio con matraces, vaso y soporte. */
export function labbench(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 46, 15);
    isoBox(g, 44, 24, 22, 0xd6dade, { stroke: 0x9aa4b0 });
    // cajones
    g.lineStyle(1.4, 0xa4adb4, 0.7);
    for (const dy of [-16, -8]) { g.beginPath(); g.moveTo(-44, dy); g.lineTo(0, dy + 24); g.strokePath(); }
    // matraces + vaso encima de la encimera (y≈-22)
    const flask = (ox: number, c: number) => {
      g.fillStyle(c, 0.85); g.fillTriangle(ox, -42, ox - 8, -22, ox + 8, -22);
      g.fillStyle(darken(c, 0.7), 0.5); g.fillTriangle(ox, -42, ox - 8, -22, ox, -22);
      g.fillStyle(0xdfe4e6, 0.9); g.fillRect(ox - 2, -46, 4, 6);
    };
    flask(-22, 0x2f8f57); flask(-6, 0xd3651a);
    g.fillStyle(0x8ac6d0, 0.7); g.fillRect(8, -34, 11, 12); g.fillStyle(0x2c6270, 0.6); g.fillRect(8, -26, 11, 4);
    // soporte con aro
    g.lineStyle(2, 0x5a5f68, 1); g.beginPath(); g.moveTo(24, -22); g.lineTo(24, -44); g.strokePath();
    g.strokeCircle(30, -36, 5);
  });
}

/** Muro de obra sin terminar: hormigón + ladrillo + ferralla saliente. */
export function wall(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    contact(g, 62, 14);
    const ph = 92;
    isoBox(g, 62, 16, ph, 0xc0b088, { stroke: 0x8a7a55 });
    // hiladas (cara izquierda SO)
    g.lineStyle(1.3, 0x8a6a44, 0.55);
    for (let r = 1; r <= 8; r++) { const y = -ph + r * 11; g.beginPath(); g.moveTo(-62, y); g.lineTo(0, y + 16); g.strokePath(); }
    // ladrillos rojizos en la parte inferior (zona ya levantada)
    g.fillStyle(0x9a5a3a, 0.5);
    for (let r = 5; r <= 7; r++) for (let c = 0; c < 3; c++) { const y = -ph + r * 11; g.fillRect(-48 + c * 17, y + 2, 13, 7); }
    // remate irregular (mortero) + ferralla saliente
    g.lineStyle(2, 0x6a6f77, 1);
    for (const rx of [-42, -20, 2]) { g.beginPath(); g.moveTo(rx, -ph + 8); g.lineTo(rx + 2, -ph - 14); g.strokePath(); }
    hiLine(g, -60, -ph + 4, -2, -ph + 20, 0xffffff, 0.16, 2);
  }, false);
}

/** Forjado / plataforma de hormigón: canto marcado + varillas + textura. */
export function platform(scene: Phaser.Scene): Phaser.GameObjects.Container {
  return container(scene, (g) => {
    isoBox(g, 96, 60, 44, 0xc6c1b3, { stroke: 0x8a8578 });
    // juntas del encofrado en la cara superior
    g.lineStyle(1.2, 0x9a9488, 0.5);
    for (let k = -2; k <= 2; k++) { g.beginPath(); g.moveTo(-80 + k * 20, -44 + 6); g.lineTo(0 + k * 20, -44 - 24); g.strokePath(); }
    // manchas/desgaste
    g.fillStyle(0x9a9488, 0.25); g.fillEllipse(-20, -58, 18, 9); g.fillEllipse(30, -50, 14, 7);
    // varillas de espera en una esquina
    g.lineStyle(2, 0x6a6f77, 1);
    for (const [rx, ry] of [[-70, -50], [-58, -44], [-46, -50]] as [number, number][]) { g.beginPath(); g.moveTo(rx, ry); g.lineTo(rx + 2, ry - 16); g.strokePath(); }
  }, false);
}

/** Trabajador (NPC) — a la MISMA escala que EPI, diferenciado (sin identificador
 *  ni libreta). shirt/vest/helmet/skin configurables. */
/**
 * Aspectos de persona (tono de piel + pelo) para reflejar la DIVERSIDAD real
 * de la plantilla. Cada combinación es realista; se elige de forma determinista
 * por el nombre/id del personaje, así que su aspecto es estable pero variado.
 */
const PEOPLE_LOOKS: { skin: number; hair: number }[] = [
  { skin: 0xf2d1b3, hair: 0x2a1e14 }, // clara · castaño oscuro
  { skin: 0xe8b48c, hair: 0x1a1a1a }, // media · negro
  { skin: 0xd49a6a, hair: 0x3a2417 }, // trigueña · castaño
  { skin: 0xb87840, hair: 0x14100c }, // morena · negro
  { skin: 0x8d5a34, hair: 0x120d08 }, // oscura · negro
  { skin: 0x6b4226, hair: 0x100c08 }, // muy oscura · negro
  { skin: 0xf0c89a, hair: 0xcdb277 }, // clara · rubia
  { skin: 0xefc199, hair: 0x9a3b1e }, // clara · pelirroja
  { skin: 0xc98e5e, hair: 0x241a10 }, // trigueña · castaño oscuro
];

/** Aspectos CURADOS por nombre: cuadran con la identidad del personaje
 *  (origen diverso, veteranos con canas). El resto se reparte por hash. */
const NAMED_LOOKS: Record<string, { skin: number; hair: number }> = {
  Wei: { skin: 0xeccaa0, hair: 0x14100c },       // rasgos asiáticos, pelo negro liso
  Youssef: { skin: 0x8d5a34, hair: 0x120d08 },   // magrebí, pelo negro
  Radu: { skin: 0xf2d1b3, hair: 0x6b4423 },      // este de Europa, castaño
  Aminata: { skin: 0x5f3b22, hair: 0x100c08 },   // subsahariana, pelo negro
  Antonio: { skin: 0xecc0a0, hair: 0x8f8f8f },   // veterano, canoso
  Nando: { skin: 0xd49a6a, hair: 0x8a8a8a },     // electricista veterano, entrecano
  Núñez: { skin: 0xe8b48c, hair: 0x6a6a6a },     // directivo mayor
};

/** Aspecto (piel + pelo) por nombre/id: curado si existe, si no determinista. */
export function personLook(seed: string): { skin: number; hair: number } {
  if (NAMED_LOOKS[seed]) return NAMED_LOOKS[seed];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffff;
  return PEOPLE_LOOKS[h % PEOPLE_LOOKS.length];
}

export function worker(scene: Phaser.Scene, opts: { shirt?: number; vest?: number; helmet?: number; skin?: number; hair?: number }): Phaser.GameObjects.Container {
  const shirt = opts.shirt ?? 0x4f6b8a;
  const vest = opts.vest ?? 0xe9b70f;
  const helmet = opts.helmet ?? 0xf0c400;
  const skin = opts.skin ?? 0xe8b48c;
  const hair = opts.hair ?? 0x3a2c1e;
  return container(scene, (g) => {
    contact(g, 20, 9);
    // piernas (pantalón oscuro)
    g.fillStyle(0x2b3550, 1); g.fillRect(-11, -22, 9, 22); g.fillRect(2, -22, 9, 22);
    g.fillStyle(0x212a42, 1); g.fillRect(4, -22, 7, 22);
    // botas con puntera de acero
    g.fillStyle(0x241d14, 1); g.fillEllipse(-6, -1, 14, 6); g.fillEllipse(6, -1, 14, 6);
    g.fillStyle(0x9aa0a8, 1); g.fillEllipse(-9, -1, 5, 3); g.fillEllipse(9, -1, 5, 3);
    // brazos (mangas de camisa) + manos
    g.fillStyle(shirt, 1); g.fillRect(-19, -40, 7, 22); g.fillRect(12, -40, 7, 22);
    g.fillStyle(skin, 1); g.fillCircle(-16, -20, 4); g.fillCircle(16, -20, 4);
    // torso: camisa
    g.fillStyle(shirt, 1); g.fillRect(-13, -42, 26, 26);
    g.fillStyle(darken(shirt, 0.82), 1); g.fillRect(3, -42, 10, 26);
    // chaleco alta visibilidad (deja ver mangas)
    g.fillStyle(vest, 1); g.fillRect(-12, -42, 24, 25);
    g.fillStyle(darken(vest, 0.85), 1); g.fillRect(3, -42, 9, 25);
    // bandas reflectantes (2 horizontales + 2 verticales)
    g.fillStyle(0xdfe4e6, 1);
    g.fillRect(-12, -34, 24, 3); g.fillRect(-12, -24, 24, 3);
    g.fillRect(-8, -42, 3.5, 25); g.fillRect(5.5, -42, 3.5, 25);
    // cinturón de herramientas
    g.fillStyle(0x241d14, 1); g.fillRect(-13, -18, 26, 4);
    g.fillStyle(0x6a4f2c, 1); g.fillRect(8, -21, 5, 9);
    // cuello + cabeza
    g.fillStyle(0xdca77e, 1); g.fillRect(-4, -46, 8, 6);
    g.fillStyle(skin, 1); g.fillCircle(0, -52, 11);
    g.fillStyle(hair, 1); g.beginPath(); g.arc(0, -54, 11, Math.PI, 0, false); g.closePath(); g.fillPath();
    g.fillStyle(0x241d14, 1); g.fillCircle(-4, -52, 1.6); g.fillCircle(4, -52, 1.6);
    // casco con ala y brillo
    g.fillStyle(helmet, 1); g.beginPath(); g.arc(0, -56, 13, Math.PI, 0, false); g.closePath(); g.fillPath();
    g.fillStyle(Math.min(0xffffff, darken(helmet, 1.12)) & 0xffffff, 1);
    g.beginPath(); g.arc(0, -57, 13, Math.PI * 1.12, Math.PI * 1.9, false); g.closePath(); g.fillPath();
    g.fillStyle(helmet, 1); g.fillRect(-15, -57, 30, 4);
    g.lineStyle(1.4, darken(helmet, 0.72), 1); g.strokeCircle(0, -56, 13);
  }, true, 20, 9);
}
